export interface OptimizationResultBadge {
  key: string;
  label: string;
  cssClass: string;
  description: string;
}

function finiteNumber(valueIn: any, fallbackIn: number = NaN): number {
  const value = Number(valueIn);
  return Number.isFinite(value) ? value : fallbackIn;
}

function readSheets(resultIn: any): number {
  return finiteNumber(resultIn && resultIn.sheets !== undefined ? resultIn.sheets : resultIn && resultIn.ns);
}

function readStacks(resultIn: any): number {
  const value = finiteNumber(
    resultIn && resultIn.stacks !== undefined ? resultIn.stacks : resultIn && resultIn.ne
  );
  return Number.isFinite(value) && value > 0 ? value : NaN;
}

function readWaste(resultIn: any): number {
  return finiteNumber(resultIn && resultIn.waste_percent !== undefined ? resultIn.waste_percent : resultIn && resultIn.tm);
}

function readRetal(resultIn: any): number {
  if (resultIn && resultIn.final_retal_area_m2 !== undefined) {
    return finiteNumber(resultIn.final_retal_area_m2);
  }
  if (resultIn && resultIn.tail_area_m2 !== undefined) {
    return finiteNumber(resultIn.tail_area_m2);
  }
  if (resultIn && resultIn.rm !== undefined) {
    return finiteNumber(resultIn.rm);
  }
  return finiteNumber(resultIn && resultIn.retal_area_m2);
}

function readComplexity(resultIn: any): number {
  if (resultIn && resultIn.cut_complexity_score !== undefined) {
    return finiteNumber(resultIn.cut_complexity_score);
  }
  return finiteNumber(resultIn && resultIn.cut_complexity && resultIn.cut_complexity.score);
}

function isCompleteResult(resultIn: any): boolean {
  if (!resultIn) {
    return false;
  }
  if (resultIn.status && resultIn.status !== 'complete') {
    return false;
  }
  const unplaced = finiteNumber(resultIn.unplaced_pieces, 0);
  return unplaced <= 0 && finiteNumber(readSheets(resultIn)) > 0;
}

function addBadge(resultIn: any, badgeIn: OptimizationResultBadge): void {
  if (!resultIn || !badgeIn) {
    return;
  }
  if (!Array.isArray(resultIn.result_badges)) {
    resultIn.result_badges = [];
  }
  if (!resultIn.result_badges.some(item => item && item.key === badgeIn.key)) {
    resultIn.result_badges.push(badgeIn);
  }
}

function sameMetric(valueIn: number, targetIn: number, toleranceIn: number = 0.0001): boolean {
  return Number.isFinite(valueIn) &&
    Number.isFinite(targetIn) &&
    Math.abs(valueIn - targetIn) <= toleranceIn;
}

function minimumFinite(resultsIn: any[], readerIn: (result: any) => number): number {
  const values = (resultsIn || []).map(readerIn).filter(value => Number.isFinite(value));
  return values.length > 0 ? Math.min.apply(null, values) : NaN;
}

function maximumFinite(resultsIn: any[], readerIn: (result: any) => number): number {
  const values = (resultsIn || []).map(readerIn).filter(value => Number.isFinite(value));
  return values.length > 0 ? Math.max.apply(null, values) : NaN;
}

function chooseBalanced(resultsIn: any[], bestWasteIn: number, bestRetalIn: number): any {
  const wasteTolerance = 0.5;
  const retalToleranceRatio = 0.90;
  const productionResults = (resultsIn || []).filter(result =>
    result && result.guillotine_absolute_performance !== true
  );
  const candidates = productionResults.length > 0 ? productionResults : (resultsIn || []);
  const preferred = candidates.filter(result => {
    const waste = readWaste(result);
    const retal = readRetal(result);
    const wasteOk = !Number.isFinite(bestWasteIn) ||
      !Number.isFinite(waste) ||
      waste <= bestWasteIn + wasteTolerance;
    const retalOk = !Number.isFinite(bestRetalIn) ||
      bestRetalIn <= 0 ||
      !Number.isFinite(retal) ||
      retal >= bestRetalIn * retalToleranceRatio;
    return wasteOk && retalOk;
  });
  const pool = preferred.length > 0 ? preferred : candidates;
  return pool.slice().sort((left, right) => {
    const stackDiff = finiteNumber(readStacks(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readStacks(right), Number.MAX_SAFE_INTEGER);
    if (stackDiff !== 0) {
      return stackDiff;
    }
    const wasteDiff = finiteNumber(readWaste(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readWaste(right), Number.MAX_SAFE_INTEGER);
    if (wasteDiff !== 0) {
      return wasteDiff;
    }
    const retalDiff = finiteNumber(readRetal(right), -1) - finiteNumber(readRetal(left), -1);
    if (retalDiff !== 0) {
      return retalDiff;
    }
    return finiteNumber(readComplexity(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readComplexity(right), Number.MAX_SAFE_INTEGER);
  })[0] || null;
}

export function classifyOptimizationResults(resultsIn: any[]): any[] {
  const results = (Array.isArray(resultsIn) ? resultsIn : []).map(result => {
    const classified = Object.assign({}, result || {});
    if (!Number.isFinite(readStacks(classified))) {
      classified.stacks = null;
      classified.stacks_unavailable = true;
    }
    classified.result_badges = [];
    classified.industrial_recommended = false;
    classified.industrial_discarded = false;
    classified.industrial_pareto_alternative = false;
    classified.industrial_discard_reason = '';
    classified.industrial_extra_sheets = 0;
    return classified;
  });
  const complete = results.filter(isCompleteResult);
  const bestSheetCount = minimumFinite(complete, readSheets);
  if (!Number.isFinite(bestSheetCount)) {
    return results;
  }

  const recommended = complete.filter(result => sameMetric(readSheets(result), bestSheetCount));
  const bestWaste = minimumFinite(recommended, readWaste);
  const bestStacks = minimumFinite(recommended, readStacks);
  const bestRetal = maximumFinite(recommended, readRetal);
  const bestComplexity = minimumFinite(recommended, readComplexity);
  const balanced = chooseBalanced(recommended, bestWaste, bestRetal);
  complete.forEach(result => {
    if (result.oef_enabled === true || result.oef_variant_index !== undefined) {
      addBadge(result, {
        key: 'oef_enabled',
        label: 'OEF activado',
        cssClass: 'is-dark',
        description: 'Resultado calculado con los primitivos OEF elegidos por el usuario.'
      });
    }
    if (result.guillotine_absolute_performance === true) {
      addBadge(result, {
        key: 'absolute_performance',
        label: 'Rendimiento absoluto',
        cssClass: 'is-dark',
        description: 'Variante para comparativas comerciales: las pilas no condicionan la búsqueda.'
      });
      addBadge(result, {
        key: 'benchmark_only',
        label: 'Solo comparativa',
        cssClass: 'is-light',
        description: 'No se recomienda automáticamente para producción; debe compararse con el equilibrio industrial.'
      });
    }
  });

  recommended.forEach(result => {
    result.industrial_recommended = true;
    if (sameMetric(readWaste(result), bestWaste)) {
      addBadge(result, {
        key: 'best_yield',
        label: 'Mejor rendimiento',
        cssClass: 'is-success',
        description: 'Mejor aprovechamiento entre los resultados con el mínimo número de hojas.'
      });
    }
    if (sameMetric(readStacks(result), bestStacks)) {
      addBadge(result, {
        key: 'fewest_stacks',
        label: 'Menos pilas',
        cssClass: 'is-info',
        description: 'Menor número de pilas entre los resultados con el mínimo número de hojas.'
      });
    }
    if (sameMetric(readRetal(result), bestRetal)) {
      addBadge(result, {
        key: 'largest_retal',
        label: 'Mayor retal final',
        cssClass: 'is-warning',
        description: 'Mayor retal final reutilizable entre los resultados recomendados.'
      });
    }
    if (sameMetric(readComplexity(result), bestComplexity)) {
      addBadge(result, {
        key: 'simplest_cut',
        label: 'Tronzado más sencillo',
        cssClass: 'is-primary',
        description: 'Menor complejidad de tronzado entre los resultados recomendados.'
      });
    }
    if (result === balanced) {
      addBadge(result, {
        key: 'best_balance',
        label: 'Mejor equilibrio',
        cssClass: 'is-link',
        description: 'Mejor equilibrio industrial entre merma, retal y número de pilas.'
      });
    }
  });

  complete.filter(result => readSheets(result) > bestSheetCount).forEach(result => {
    const extraSheets = Math.max(1, Math.round(readSheets(result) - bestSheetCount));
    result.industrial_discarded = true;
    result.industrial_extra_sheets = extraSheets;
    result.industrial_discard_reason = 'Utiliza ' + extraSheets + ' hoja' +
      (extraSheets === 1 ? '' : 's') + ' adicional' + (extraSheets === 1 ? '' : 'es') + '.';
    addBadge(result, {
      key: 'discarded_consumption',
      label: 'Descartada por consumo',
      cssClass: 'is-danger',
      description: result.industrial_discard_reason
    });
    const stacks = readStacks(result);
    if (Number.isFinite(stacks) && Number.isFinite(bestStacks) && stacks < bestStacks) {
      const sheets = readSheets(result);
      const isDominated = complete.some(other => {
        if (!other || other === result) {
          return false;
        }
        const otherSheets = readSheets(other);
        const otherStacks = readStacks(other);
        return Number.isFinite(otherSheets) &&
          Number.isFinite(otherStacks) &&
          otherSheets <= sheets &&
          otherStacks <= stacks &&
          (otherSheets < sheets || otherStacks < stacks);
      });
      const stackSaving = Math.max(1, Math.round(bestStacks - stacks));
      addBadge(result, {
        key: 'fewer_stacks_extra_sheet',
        label: 'Menos pilas, pero +' + extraSheets + ' hoja' + (extraSheets === 1 ? '' : 's'),
        cssClass: 'is-light',
        description: 'Reduce pilas, pero queda fuera del óptimo industrial por consumir más vidrio.'
      });
      if (!isDominated) {
        result.industrial_pareto_alternative = true;
        addBadge(result, {
          key: 'industrial_pareto_alternative',
          label: 'Alternativa: -' + stackSaving + ' pila' + (stackSaving === 1 ? '' : 's') +
            ' / +' + extraSheets + ' hoja' + (extraSheets === 1 ? '' : 's'),
          cssClass: 'is-warning',
          description: 'Alternativa no dominada: reduce pilas a cambio de consumir más hojas.'
        });
      }
    }
  });

  return results.sort((left, right) => {
    if (left.industrial_recommended !== right.industrial_recommended) {
      return left.industrial_recommended ? -1 : 1;
    }
    const sheetDiff = finiteNumber(readSheets(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readSheets(right), Number.MAX_SAFE_INTEGER);
    if (sheetDiff !== 0) {
      return sheetDiff;
    }
    const wasteDiff = finiteNumber(readWaste(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readWaste(right), Number.MAX_SAFE_INTEGER);
    if (wasteDiff !== 0) {
      return wasteDiff;
    }
    return finiteNumber(readStacks(left), Number.MAX_SAFE_INTEGER) -
      finiteNumber(readStacks(right), Number.MAX_SAFE_INTEGER);
  });
}

export function getRecommendedOptimizationResults(resultsIn: any[]): any[] {
  return classifyOptimizationResults(resultsIn).filter(result => result && result.industrial_recommended === true);
}

export function getDiscardedOptimizationResults(resultsIn: any[]): any[] {
  return classifyOptimizationResults(resultsIn).filter(result => result && result.industrial_discarded === true);
}

export function compactOptimizationResultFrontier(resultsIn: any[], limitIn: number = 12): any[] {
  const limit = Math.max(1, Math.round(finiteNumber(limitIn, 12)));
  const classified = classifyOptimizationResults(resultsIn);
  const recommended = classified.filter(result => result && result.industrial_recommended === true);
  const discardedWithBenefit = classified.filter(result =>
    result &&
    result.industrial_discarded === true &&
    Array.isArray(result.result_badges) &&
    result.result_badges.some(badge => badge && badge.key === 'fewer_stacks_extra_sheet')
  );
  const selected: any[] = [];
  const selectedKeys = {};

  const add = (resultIn: any): void => {
    if (!resultIn || selected.length >= limit) {
      return;
    }
    const key = [
      resultIn.__optimizer_result_index !== undefined ? resultIn.__optimizer_result_index : '',
      resultIn.strategy || '',
      readSheets(resultIn),
      readStacks(resultIn),
      readWaste(resultIn),
      readRetal(resultIn),
      resultIn.maxtrav !== undefined ? resultIn.maxtrav : ''
    ].join('|');
    if (!selectedKeys[key]) {
      selectedKeys[key] = true;
      selected.push(resultIn);
    }
  };

  recommended.filter(result =>
    Array.isArray(result.result_badges) &&
    result.result_badges.some(badge => badge && badge.key !== 'oef_enabled')
  ).forEach(add);

  const stacksSeen = {};
  recommended.forEach(result => {
    const stackKey = readStacks(result).toString();
    if (!stacksSeen[stackKey]) {
      stacksSeen[stackKey] = true;
      add(result);
    }
  });
  recommended.forEach(add);
  const discardedReserve = Math.min(2, discardedWithBenefit.length);
  if (discardedReserve > 0 && selected.length > limit - discardedReserve) {
    selected.splice(limit - discardedReserve);
  }
  discardedWithBenefit.forEach(add);

  if (selected.length < 1) {
    classified.slice(0, limit).forEach(add);
  }
  return classifyOptimizationResults(selected);
}
